// Shared pieces of the adscription screens (no React, relative imports only: the web test runner transpiles each
// file on its own): unit names, the payloads of the request and of the admin direct edit, and the wording of a
// replaced request. The staging of the modal itself (rows, what applies at once, the button, the reasons) lives in
// `adscripcionEdicion`; the domain core (validation, diff, effect) lives in `@siiv/schemas`.

import { MOTIVO_ITEM_MAX_LARGO, esUnidadOtra, type CreateSolicitudInput, type EstadoAdscripcion } from '@siiv/schemas';

/** Longest reason of ONE change (the limit the API enforces on each entry of `motivos`). */
export const MOTIVO_MAX_LARGO = MOTIVO_ITEM_MAX_LARGO;
/** Longest custom name an "Otra" unit may carry. */
export const NOMBRE_OTRA_MAX_LARGO = 120;
/**
 * Size of the adscription modal card: the one of the other edit modals (collections: 1180px wide, 720px high like
 * "Formación académica"), so the screen has room for the rows and for the form block that opens inside the list. The card is
 * still capped at 90% of the viewport in both directions, and it never gets narrower than `ANCHO_MIN_MODAL_ADSCRIPCION`
 * (or the viewport, on a phone) so it does not look cramped.
 */
export const ANCHO_MODAL_ADSCRIPCION = '1180px';
export const ALTO_MODAL_ADSCRIPCION = '720px';
export const ANCHO_MIN_MODAL_ADSCRIPCION = '720px';

export interface UnidadCatalogoMin {
	idUnidad: number;
	nombreUnidad: string | null;
	nombreAbreviado: string | null;
}

/** `solicitud`: a researcher asks and an admin resolves. `directo`: an admin applies the state at once. */
export type ModoAdscripcion = 'solicitud' | 'directo';

/** The free text that travels with a staged edition: the name of a new "Otra" and the reason of a request. */
export interface FormularioAdscripcion {
	nombreOtra: string;
	/** The reason of each structural change, keyed by item id. */
	motivos: Record<string, string>;
}

/** The current unit state out of `GET :slug/adscripcion`, or null for a researcher with no principal. */
export function estadoDesdeContexto(contexto: {
	principal: { idUnidad: number } | null;
	adicionales: { idUnidad: number }[];
}): EstadoAdscripcion | null {
	if (!contexto.principal) return null;
	return {
		idUnidadPrincipal: contexto.principal.idUnidad,
		idUnidadesAdicionales: contexto.adicionales.map((unidad) => unidad.idUnidad),
	};
}

/** Display name of a unit: name, then abbreviation, then the id. */
export function etiquetaUnidad(unidad: UnidadCatalogoMin | undefined, idUnidad: number): string {
	const nombre = unidad?.nombreUnidad?.trim();
	if (nombre) return nombre;
	const abreviado = unidad?.nombreAbreviado?.trim();
	if (abreviado) return abreviado;
	return `Unidad ${idUnidad}`;
}

/** `id -> name` for every catalog unit; the generic "Otra" shows the custom name when one was typed. */
export function nombresDeUnidades(catalogo: UnidadCatalogoMin[], nombreOtra?: string | null): Map<number, string> {
	const nombres = new Map(catalogo.map((unidad) => [unidad.idUnidad, etiquetaUnidad(unidad, unidad.idUnidad)]));
	const personalizado = nombreOtra?.trim();
	if (personalizado) {
		for (const unidad of catalogo) {
			if (esUnidadOtra(unidad.idUnidad)) nombres.set(unidad.idUnidad, personalizado);
		}
	}
	return nombres;
}

const hayOtra = (estado: EstadoAdscripcion): boolean =>
	[estado.idUnidadPrincipal, ...estado.idUnidadesAdicionales].some(esUnidadOtra);

/** `nombreOtra` as the API expects it: trimmed text, or null (keep the stored name) when blank or not needed. */
function nombreOtraParaEnviar(objetivo: EstadoAdscripcion, form: FormularioAdscripcion): string | null {
	if (!hayOtra(objetivo)) return null;
	return form.nombreOtra.trim() || null;
}

/** The reasons as the API expects them: trimmed, and only the ones that were written (the server names what is missing). */
function motivosParaEnviar(form: FormularioAdscripcion): Record<string, string> {
	return Object.fromEntries(
		Object.entries(form.motivos)
			.map(([idItem, motivo]) => [idItem, motivo.trim()] as const)
			.filter(([, motivo]) => motivo !== ''),
	);
}

/**
 * `POST /solicitudes` body. Items are never sent: the server derives them from the current state and attaches the reason
 * of each one (`motivos`, keyed by item id). There is no single justification: the server builds it from the reasons.
 */
export function construirPayloadSolicitud(
	objetivo: EstadoAdscripcion,
	form: FormularioAdscripcion,
): CreateSolicitudInput {
	return {
		tipoSolicitud: 'cambio_unidad',
		adscripcion: { objetivo, nombreOtra: nombreOtraParaEnviar(objetivo, form), motivos: motivosParaEnviar(form) },
	};
}

/** `PUT :slug/adscripcion` body (admin direct mode). */
export function construirPayloadDirecto(
	objetivo: EstadoAdscripcion,
	form: FormularioAdscripcion,
): EstadoAdscripcion & { nombreOtra: string | null } {
	return {
		idUnidadPrincipal: objetivo.idUnidadPrincipal,
		idUnidadesAdicionales: objetivo.idUnidadesAdicionales,
		nombreOtra: nombreOtraParaEnviar(objetivo, form),
	};
}

/**
 * Toast description after the server replaced pending requests with the new one. It does not name them: the number of a
 * request is bookkeeping, not something the researcher needs.
 */
export function descripcionReemplazo(idsReemplazadas: number[]): string | null {
	if (idsReemplazadas.length === 0) return null;
	return idsReemplazadas.length === 1
		? 'Reemplazó tu solicitud anterior, que estaba pendiente.'
		: 'Reemplazó tus solicitudes anteriores, que estaban pendientes.';
}
